/**
 * @file  src/services/v2/pendingImportsService.js
 * @brief MLS saved-search "pending import" API calls — lets an agent turn a
 *        connectMLS saved search MIBOR forwards us into a prefilled draft on
 *        the existing Want-Ad creation form (see AgentCreateWantAd.js).
 */
import * as endPoints from "../../constants/v2/endPoints";
import * as apiService from "../v2/apiService";
import { WANT_AD_BUY_PROPERTY_ID } from "../../constants/common";

export async function getPendingImports() {
  return await apiService.apiCall(endPoints.GET_PENDING_IMPORTS, "GET", {});
}

export async function getPendingImport(id) {
  return await apiService.apiCall(
    endPoints.GET_PENDING_IMPORT.replace(":id", id),
    "GET",
    {},
  );
}

// Marks the import claimed server-side (protects it from a later saved-
// search re-sync) and returns the prefill payload to seed AgentCreateWantAd
// with. `clientId` is optional — pass it when the agent has already picked
// an existing client to link this MLS identity to.
export async function startPendingImport(id, clientId) {
  return await apiService.apiCall(
    endPoints.START_PENDING_IMPORT.replace(":id", id),
    "POST",
    clientId ? { clientId } : {},
  );
}

// Best-effort traceability call after the agent actually saves the
// prefilled draft as a real Want-Ad. Never block or surface an error from
// this to the agent — the want-ad save already succeeded by this point.
export async function linkPendingImportWantAd(id, wantAdId) {
  return await apiService.apiCall(
    endPoints.LINK_PENDING_IMPORT_WANT_AD.replace(":id", id),
    "POST",
    { wantAdId },
  );
}

// Runs on the save that creates the want-ad from an import: claims the
// import server-side (it is only previewed, not claimed, by "Start from this
// search" — see PendingImportCards.js) and links the two,
// both best-effort since the want-ad save has already succeeded by this
// point. Also drops the prefill from router history state: AgentCreateWantAd
// prefers `location.state.data` over localStorage, and history state
// survives a reload — left in place, a refresh after the first autosave
// would rebuild the form without its wantAdId and the next autosave would
// create a second want-ad.
export function handlePendingImportWantAdCreated(
  pendingImportId,
  wantAdId,
  navigate,
  location,
  queryClient,
) {
  // Sequenced, not parallel: the server only links a want-ad to an import that
  // is already claimed, so a link that overtook the claim would be dropped.
  // A failed claim still falls through to the link attempt.
  startPendingImport(pendingImportId)
    .catch((err) => console.log("Error", err))
    // Only now is the row off the server's pending list. Reset rather than
    // invalidate: the cached list (5-minute staleTime) still holds the
    // claimed card, and an invalidated entry is handed to the next mount
    // while its refetch runs — letting the agent Start the same import again
    // and create a second want-ad.
    .then(() => queryClient?.resetQueries({ queryKey: ["pendingImports"] }))
    .then(() => linkPendingImportWantAd(pendingImportId, wantAdId))
    .catch((err) => console.log("Error", err));
  if (location?.state?.data) {
    navigate(`${location.pathname}${location.search || ""}`, {
      replace: true,
      state: null,
    });
  }
}

export async function dismissPendingImport(id) {
  return await apiService.apiCall(
    endPoints.DISMISS_PENDING_IMPORT.replace(":id", id),
    "POST",
    {},
  );
}

// AgentWantAdForm seeds its wantAdData state from this prefill and later
// posts wantAdData as-is as the save payload (and persists it to
// AGENT_WANTAD_KEY) — pendingImportId, unmappedFieldCount and assumed exist
// only to drive the pending-import UI/traceability call and must never ride
// along. `assumed` is the API's list of guesses ({ key, note }) for the banner.
//
// The sections below the property category are gated on `prprtyType`, which the
// category radio sets to the buy marker. A prefilled category skips the radio,
// so the marker is set here or the rest of the form stays hidden.
export function splitPendingImportPrefill(initialData) {
  const { pendingImportId, unmappedFieldCount, assumed, ...wantAdSeed } =
    initialData || {};
  // Imports only: an existing draft or edit is never rewritten.
  if (
    pendingImportId != null &&
    wantAdSeed.propertyCategory &&
    !wantAdSeed.prprtyType
  ) {
    wantAdSeed.prprtyType = WANT_AD_BUY_PROPERTY_ID;
    wantAdSeed.prptyTypeTxt = "BUY";
  }
  return {
    wantAdSeed,
    pendingImportId: pendingImportId ?? null,
    assumed: Array.isArray(assumed) ? assumed : [],
  };
}

// Criteria the saved-search crosswalk can prefill (the API's mapped keys, see
// mls-etl/src/transform/savedSearch.ts). The form's first required step —
// picking a property category — rebuilds wantAdData from a fixed field list
// and would drop all of these, so an import-seeded form carries them over.
const PENDING_IMPORT_CRITERIA_KEYS = [
  "minBdgt",
  "maxBdgt",
  "noBedRm",
  "noBathRm",
  "sqFtMin",
  "sqFtMax",
  "yearBuiltOption",
  "yrBltMin",
  "yrBltMax",
  // Not in the form's fixed field list, so choosing a category would drop it.
  "adTitle",
];

export function pickPendingImportCriteria(wantAdData) {
  const picked = {};
  PENDING_IMPORT_CRITERIA_KEYS.forEach((key) => {
    if (wantAdData?.[key] !== undefined) picked[key] = wantAdData[key];
  });
  return picked;
}
